/**
 * studioProtocols.ts — Generated Continue and Sync protocols for Studio OS V2.
 *
 * Protocols are produced at render time from shared templates and workspace data.
 * Updating a template here improves all workspaces simultaneously.
 *
 * Workspace-specific behaviour is injected via:
 *   branch            — working branch for this workspace
 *   ownership         — structured map of what this workspace is responsible for
 *   constraints       — workspace-specific guard rails
 *   extraVerification — additional verification steps for Sync Phase 4
 */

import type { Workspace, WorkspaceOwnership } from './agentCommandCenter';

// ─── Templates ───────────────────────────────────────────────────────────────

const CONTINUE_TEMPLATE = `\
=== STUDIO OS — CONTINUE PROTOCOL V2 ===
Workspace: {name} {emoji}

─── PHASE 1 · BOOTSTRAP ──────────────────────────────────────────
1. Read the workspace passport at:
     {passportPath}
2. Read this workspace's entry in:
     frontend/src/data/agentCommandCenter.ts  (id: "{id}")
3. Compare the two. They must agree on: health, knowledgeStatus,
   currentMission, lastWin, nextAction, blockers, pendingDecisions.
4. If drift is detected:
     a. List every field that disagrees.
     b. Propose the correct value for each.
     c. STOP. Do not proceed to Phase 2 until drift is resolved.

─── PHASE 2 · OWNERSHIP VERIFICATION ───────────────────────────
This workspace is responsible for:

{ownershipTable}

Before modifying any file, verify it belongs to one of the owned
domains above. If a required file belongs to another workspace:
  • STOP immediately.
  • Report: the file, the owning workspace, the recommended handoff.
  • Do NOT modify the file.
  • Do NOT continue the current task.

─── PHASE 3 · WORKSPACE CONTEXT ─────────────────────────────────
Determine from the passport and source files:
  • Current Mission
  • Last Win
  • Next Action
  • Pending Decisions
  • Technical Debt (if tracked)
  • Current git branch        (expected: {branch})
  • Working tree status       (run: git status)

─── PHASE 4 · READINESS CHECK ───────────────────────────────────
Verify all of the following before continuing:
  [ ] On branch:              {branch}
  [ ] Working tree clean      (no uncommitted changes unless expected)
  [ ] Passport in sync with ACC
If any check fails: report it and pause.

─── PHASE 5 · RESUME ────────────────────────────────────────────
Continue directly with Next Action from Phase 3.
Do not restart architectural reviews.
Do not recreate the workspace.
Do not ask the user to restate context.
The user pressed Continue — they expect work to resume, not explanations.

─── WORKSPACE CONSTRAINTS ───────────────────────────────────────
{constraints}`;

const SYNC_TEMPLATE = `\
=== STUDIO OS — SYNC PROTOCOL V2 ===
Workspace: {name} {emoji}

─── PHASE 1 · SESSION REVIEW ────────────────────────────────────
Review all meaningful work completed this session.
List: files changed, decisions made, milestones reached.

─── PHASE 2 · UPDATE PASSPORT ───────────────────────────────────
Update the workspace passport at:
  {passportPath}

Fields to update:
  • Last Updated → today's date
  • Health, Knowledge Status, Last Sync
  • Current Mission
  • Last Win  (what shipped or was proven this session)
  • Next Action  (the very next concrete step)
  • Architectural milestones  (if any were crossed)
  • Technical Debt  (add new items; remove resolved items)
  • Blockers, Pending Decisions

─── PHASE 3 · UPDATE ACC ────────────────────────────────────────
Update ONLY the entry with id: "{id}" in:
  frontend/src/data/agentCommandCenter.ts

Mirror the same fields updated in Phase 2.
Never modify any other workspace's entry.

─── PHASE 4 · VERIFICATION ──────────────────────────────────────
Run project verification:
  npx tsc --noEmit        (must pass with zero errors)
  npm run build           (must complete cleanly)
{extraVerification}
─── PHASE 5 · OWNERSHIP VERIFICATION ───────────────────────────
Before preparing the commit, confirm every modified file belongs
to this workspace:

{ownershipTable}

  [ ] All modified files belong to the owned domains above
  [ ] No files from other workspaces were modified
  [ ] No cross-workspace modifications occurred
  [ ] No accidental edits to unrelated code
  [ ] No generated artifacts staged (dist/, *.zip, node_modules/)

If any ownership violation exists:
  • STOP. Do not prepare the commit.
  • Report every violation with the file and owning workspace.
  • Propose corrections before proceeding.

─── PHASE 6 · GIT PREPARATION ───────────────────────────────────
Confirm before staging:
  [ ] On branch:          {branch}
  [ ] Only expected files are modified
  [ ] No accidental files (dist/, node_modules/, *.zip, .env)
  [ ] No generated artifacts

─── PHASE 7 · COMMIT ────────────────────────────────────────────
Create a commit. Do not push automatically.

─── PHASE 8 · SYNC REPORT ───────────────────────────────────────
Produce a synchronization report:
  • Files changed
  • Passport updated:   {passportPath}
  • ACC entry updated:  id "{id}"
  • Commit hash
  • Git status
  • Readiness to push

─── WORKSPACE CONSTRAINTS ───────────────────────────────────────
{constraints}`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const OWNERSHIP_LABELS: [keyof WorkspaceOwnership, string][] = [
  ['reactUI',        'React UI        src/screens, src/components (non-Studio-OS)'],
  ['backend',        'Backend         backend/ — API routes, migrations, database'],
  ['gameplay',       'Gameplay        Phaser engine, game logic, sound'],
  ['studioOs',       'Studio OS       /studio route, AgentCommandCenter, agentCommandCenter.ts'],
  ['infrastructure', 'Infrastructure  auth, Firebase config, CI/CD, hosting, security rules'],
  ['passportSystem', 'Passport System studio/workspaces/ architecture and format standard'],
];

function formatOwnership(ownership: WorkspaceOwnership): string {
  return OWNERSHIP_LABELS
    .map(([key, label]) => `  ${ownership[key] ? '✓' : '✗'} ${label}`)
    .join('\n');
}

function formatConstraints(items: string[]): string {
  return items.length > 0
    ? items.map(c => `• ${c}`).join('\n')
    : 'None — follow general Studio OS guidelines.';
}

function formatExtraVerification(items: string[]): string {
  return items.length > 0
    ? items.map(v => `  ${v}`).join('\n') + '\n'
    : '';
}

function inject(template: string, vars: Record<string, string>): string {
  return Object.entries(vars).reduce(
    (t, [key, val]) => t.replace(new RegExp(`\\{${key}\\}`, 'g'), val),
    template,
  );
}

// ─── Generators ──────────────────────────────────────────────────────────────

export function generateContinueProtocol(ws: Workspace): string {
  return inject(CONTINUE_TEMPLATE, {
    name:           ws.name,
    emoji:          ws.emoji,
    passportPath:   ws.passportPath,
    id:             ws.id,
    branch:         ws.state.branch,
    ownershipTable: formatOwnership(ws.ownership),
    constraints:    formatConstraints(ws.state.constraints),
  });
}

export function generateSyncProtocol(ws: Workspace): string {
  return inject(SYNC_TEMPLATE, {
    name:              ws.name,
    emoji:             ws.emoji,
    passportPath:      ws.passportPath,
    id:                ws.id,
    branch:            ws.state.branch,
    ownershipTable:    formatOwnership(ws.ownership),
    extraVerification: formatExtraVerification(ws.state.extraVerification),
    constraints:       formatConstraints(ws.state.constraints),
  });
}
